import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from 'react-bootstrap';
import Swal from 'sweetalert2';
import { Loading } from '../../../../ui/loading/Loading';
import { zynity_api } from '../../../../../config/external-routes';
import { ContentEditorModal, ContentEditorValues } from './ContentEditorModal';
import { CommentsAndAttachments } from './CommentsAndAttachments';
import { DocumentLibraryPickerItem } from '../../../../ui/tools/DocumentLibraryPickerModal';
import { fetchLibraryContextItems, syncLibraryContextItems, STRATEGY_ITEM_CONTEXT_TYPE } from './libraryContext';

type DetailRow = {
    id: number;
    title: string | null;
    description: string | null;
    type: 'item' | 'subitem' | 'message';
    parent_id: number | null;
    listOrder: number | null;
};

type ModalMode = 'create-item' | 'edit-item' | 'create-subitem' | 'edit-subitem' | 'create-message' | 'edit-message';

type ModalState = { mode: ModalMode; parentId?: number; editing?: DetailRow };

type Props = {
    organizationId: string;
    teamId: string;
    initiativeId: string;
    allowEdit: boolean;
    currentUserId?: number;
};

/**
 * Details accordion — items/subitems/discussion topics, add/edit/delete/reorder
 * (§4b), plus Comments/Links/Uploads on every row (§4d) via the shared
 * CommentsAndAttachments panel.
 */
export const DetailsAccordion = ({ organizationId, teamId, initiativeId, allowEdit, currentUserId }: Props) => {
    const [items, setItems] = useState<DetailRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [expandedId, setExpandedId] = useState<number | null>(null);
    const [expandedTab, setExpandedTab] = useState<'content' | 'discussion'>('content');
    const [expandedSubitemId, setExpandedSubitemId] = useState<number | null>(null);
    const [expandedMessageId, setExpandedMessageId] = useState<number | null>(null);
    const [childCache, setChildCache] = useState<Record<number, { subitems: DetailRow[]; messages: DetailRow[] }>>({});
    const [childLoading, setChildLoading] = useState<number | null>(null);
    const [modal, setModal] = useState<ModalState | null>(null);
    // Confirmed live 2026-08-27: DETAILS/NEXT STEPS/MILESTONES are collapsed by
    // default (worksheet_v1.php's `ws-v1-details-section-header` bars) — React had
    // no section-level collapse at all, only the per-item expand already below.
    const [sectionOpen, setSectionOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [modalExistingLinks, setModalExistingLinks] = useState<{ id: number; url: string }[]>([]);
    const [modalExistingUploads, setModalExistingUploads] = useState<{ id: number; fileName: string; displayName: string }[]>([]);
    const [modalInitialLibraryItems, setModalInitialLibraryItems] = useState<DocumentLibraryPickerItem[]>([]);
    const [modalLoadingAttachments, setModalLoadingAttachments] = useState(false);

    const baseUrl = zynity_api.organization_team_strategies_details
        .replace('{organization_id}', organizationId)
        .replace('{team_id}', teamId)
        .replace('{initiative_id}', initiativeId);

    const itemUrl = (detailId: number) =>
        zynity_api.organization_team_strategies_detail_item
            .replace('{organization_id}', organizationId)
            .replace('{team_id}', teamId)
            .replace('{initiative_id}', initiativeId)
            .replace('{detail_id}', String(detailId));

    const reorderUrl = zynity_api.organization_team_strategies_details_reorder
        .replace('{organization_id}', organizationId)
        .replace('{team_id}', teamId)
        .replace('{initiative_id}', initiativeId);

    const attachmentsUrl = zynity_api.organization_team_strategies_attachments
        .replace('{organization_id}', organizationId)
        .replace('{team_id}', teamId)
        .replace('{initiative_id}', initiativeId);

    const attachmentLinksUrl = zynity_api.organization_team_strategies_attachment_links
        .replace('{organization_id}', organizationId)
        .replace('{team_id}', teamId)
        .replace('{initiative_id}', initiativeId);

    const attachmentUploadsUrl = zynity_api.organization_team_strategies_attachment_uploads
        .replace('{organization_id}', organizationId)
        .replace('{team_id}', teamId)
        .replace('{initiative_id}', initiativeId);

    const displayFileName = (fileName: string) => {
        const parts = fileName.split('_');
        return parts.length > 3 ? parts.slice(3).join('_') : fileName;
    };

    // Details items get inline Library/Links/Uploads fields on the Edit modal too
    // (confirmed live 2026-08-26, closing a Phase 7 scope reduction) — unlike
    // Category/Topic, whose Library lives only in the persistent expand panel.
    // Existing links/uploads are fetched here so the modal can show them read-only;
    // removing an existing one is still only done via the expand panel below (see
    // ContentEditorModal's docblock for why that's not duplicated here).
    const openEditModal = async (mode: 'edit-item' | 'edit-subitem' | 'edit-message', row: DetailRow) => {
        setModal({ mode, editing: row });
        setModalLoadingAttachments(true);
        try {
            const [attachmentsResponse, libraryDocs] = await Promise.all([
                axios.get(attachmentsUrl, { params: { detail_id: row.id } }),
                fetchLibraryContextItems(organizationId, teamId, STRATEGY_ITEM_CONTEXT_TYPE, row.id),
            ]);
            setModalExistingLinks(
                (attachmentsResponse.data.links ?? []).map((link: { id: number; links: string }) => ({ id: link.id, url: link.links }))
            );
            setModalExistingUploads(
                (attachmentsResponse.data.uploads ?? []).map((upload: { id: number; fileName: string }) => ({
                    id: upload.id,
                    fileName: upload.fileName,
                    displayName: displayFileName(upload.fileName),
                }))
            );
            setModalInitialLibraryItems(libraryDocs);
        } catch (error) {
            setModalExistingLinks([]);
            setModalExistingUploads([]);
            setModalInitialLibraryItems([]);
        } finally {
            setModalLoadingAttachments(false);
        }
    };

    const fetchItems = () => {
        setLoading(true);
        axios
            .get(baseUrl)
            .then((response) => setItems(response.data.details ?? []))
            .catch(() => setItems([]))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        fetchItems();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initiativeId]);

    const fetchChildren = (itemId: number) => {
        setChildLoading(itemId);
        axios
            .get(itemUrl(itemId))
            .then((response) => {
                setChildCache((current) => ({
                    ...current,
                    [itemId]: { subitems: response.data.subitems ?? [], messages: response.data.messages ?? [] },
                }));
            })
            .catch(() => {
                setChildCache((current) => ({ ...current, [itemId]: { subitems: [], messages: [] } }));
            })
            .finally(() => setChildLoading(null));
    };

    const toggleExpand = (itemId: number) => {
        if (expandedId === itemId) {
            setExpandedId(null);
            return;
        }
        setExpandedId(itemId);
        setExpandedTab('content');
        if (!childCache[itemId]) {
            fetchChildren(itemId);
        }
    };

    const moveItem = (index: number, direction: -1 | 1) => {
        const targetIndex = index + direction;
        if (targetIndex < 0 || targetIndex >= items.length) {
            return;
        }
        const reordered = [...items];
        [reordered[index], reordered[targetIndex]] = [reordered[targetIndex], reordered[index]];
        setItems(reordered);
        axios.put(reorderUrl, { ordered_ids: reordered.map((row) => row.id) }).catch(() => {
            Swal.fire({ icon: 'error', title: 'Reorder failed', text: 'This could not be reordered.' });
            fetchItems();
        });
    };

    const moveChild = (itemId: number, list: 'subitems' | 'messages', index: number, direction: -1 | 1) => {
        const current = childCache[itemId];
        if (!current) {
            return;
        }
        const rows = current[list];
        const targetIndex = index + direction;
        if (targetIndex < 0 || targetIndex >= rows.length) {
            return;
        }
        const reordered = [...rows];
        [reordered[index], reordered[targetIndex]] = [reordered[targetIndex], reordered[index]];
        setChildCache((state) => ({ ...state, [itemId]: { ...state[itemId], [list]: reordered } }));
        axios.put(reorderUrl, { ordered_ids: reordered.map((row) => row.id) }).catch(() => {
            Swal.fire({ icon: 'error', title: 'Reorder failed', text: 'This could not be reordered.' });
            fetchChildren(itemId);
        });
    };

    const removeRow = async (row: DetailRow, onSuccess: () => void) => {
        const result = await Swal.fire({
            title: 'Remove this?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Remove',
        });
        if (!result.isConfirmed) {
            return;
        }
        try {
            await axios.delete(itemUrl(row.id));
            onSuccess();
        } catch (error) {
            Swal.fire({ icon: 'error', title: 'Delete failed', text: 'This could not be removed.' });
        }
    };

    // Links/Uploads/Library submitted from the modal (Phase 6/7 pattern, extended to
    // Details items 2026-08-27) can only attach once the row has an id, so — matching
    // the same create-then-attach sequence already used for Category/Topic — fire
    // these off right after the create/edit call succeeds, before refreshing.
    const applyModalAttachments = async (entityId: number, values: ContentEditorValues) => {
        await Promise.all([
            ...(values.links ?? []).map((url) => axios.post(attachmentLinksUrl, { url, detail_id: entityId })),
            ...(values.uploadedFileNames ?? []).map((file_name) =>
                axios.post(attachmentUploadsUrl, { file_name, detail_id: entityId })
            ),
        ]);
        if (values.libraryItemIds) {
            await syncLibraryContextItems(
                organizationId,
                teamId,
                STRATEGY_ITEM_CONTEXT_TYPE,
                entityId,
                `Strategies / detail_id: ${entityId}`,
                values.libraryItemIds
            );
        }
    };

    const handleModalSubmit = async (values: ContentEditorValues) => {
        if (!modal) {
            return;
        }
        setSaving(true);
        try {
            if (modal.mode === 'create-item') {
                const response = await axios.post(baseUrl, { title: values.title, description: values.description || null });
                await applyModalAttachments(response.data.detail.id, values);
                fetchItems();
            } else if (modal.mode === 'create-subitem' && modal.parentId) {
                const response = await axios.post(baseUrl, {
                    title: values.title,
                    description: values.description || null,
                    parent_id: modal.parentId,
                    as: 'subitem',
                });
                await applyModalAttachments(response.data.detail.id, values);
                fetchChildren(modal.parentId);
            } else if (modal.mode === 'create-message' && modal.parentId) {
                const response = await axios.post(baseUrl, {
                    title: values.title,
                    description: values.description || null,
                    parent_id: modal.parentId,
                    as: 'message',
                });
                await applyModalAttachments(response.data.detail.id, values);
                fetchChildren(modal.parentId);
            } else if (modal.editing) {
                await axios.put(itemUrl(modal.editing.id), {
                    title: values.title,
                    description: values.description || null,
                });
                await applyModalAttachments(modal.editing.id, values);
                if (modal.editing.type === 'item') {
                    fetchItems();
                } else if (modal.editing.parent_id) {
                    fetchChildren(modal.editing.parent_id);
                }
            }
            setModal(null);
        } catch (error) {
            Swal.fire({ icon: 'error', title: 'Save failed', text: 'This could not be saved.' });
        } finally {
            setSaving(false);
        }
    };

    // Titles confirmed live 2026-08-26 (worksheet Details accordion, not the
    // Categories-board modal titles, which use different strings) — "New Details
    // Item"/"Edit Item". Subitem strings independently re-confirmed 2026-08-27:
    // live actually says "Add Subitem"/"Edit Subitem" (not "New Subitem"), and the
    // field itself is labeled/placeholder'd "Subitem *", not the generic "Title *"
    // — see the titleLabel/titlePlaceholder props on ContentEditorModal below.
    // Topic-within-item strings independently re-confirmed 2026-08-27 too, by
    // actually opening the live modal (not just grepping worksheet_v1.js's
    // showAddDiscussionModal, which was misread earlier as "New Topic" — that
    // string is the *Categories-board* Topic modal's title, a different, unrelated
    // modal): live says "Add Topic" here, not "New Topic", and the field is
    // labeled/placeholder'd "Topic *", same pattern as Subitem. The Story field also
    // differs live from every other mode here: it's labeled "Story" on create but
    // "Description" on edit (showEditDiscussionTopicModal relabels it), and always
    // placeholders "Description (optional)" — see storyLabel/storyPlaceholder below.
    const modalTitle = (mode: ModalMode) => {
        switch (mode) {
            case 'create-item':
                return 'New Details Item';
            case 'edit-item':
                return 'Edit Item';
            case 'create-subitem':
                return 'Add Subitem';
            case 'edit-subitem':
                return 'Edit Subitem';
            case 'create-message':
                return 'Add Topic';
            case 'edit-message':
                return 'Edit Topic';
            default:
                return '';
        }
    };

    const isSubitemModal = modal?.mode === 'create-subitem' || modal?.mode === 'edit-subitem';
    const isMessageModal = modal?.mode === 'create-message' || modal?.mode === 'edit-message';

    if (loading) {
        return <Loading />;
    }

    return (
        <div className="strategies-details-accordion strategies-section--details mt-4">
            <div
                className="strategies-section-toggle d-flex justify-content-between align-items-center"
                role="button"
                aria-expanded={sectionOpen}
                data-testid="section-toggle"
                onClick={() => setSectionOpen((current) => !current)}
            >
                <h4 className="mb-0">Details</h4>
                <span className="strategies-section-chevron">{sectionOpen ? '▾' : '▸'}</span>
            </div>

            {sectionOpen && (
            <>
            <div className="mb-2 mt-3">
                {allowEdit && (
                    <Button className="strategies-add-detail-btn" onClick={() => {
                        setModalExistingLinks([]);
                        setModalExistingUploads([]);
                        setModalInitialLibraryItems([]);
                        setModal({ mode: 'create-item' });
                    }}>
                        + Add Detail
                    </Button>
                )}
            </div>

            {items.length === 0 ? (
                <p className="text-muted">No details yet.</p>
            ) : (
                <div className="d-flex flex-column gap-2">
                    {items.map((item, index) => {
                        const children = childCache[item.id];
                        const messageCount = children?.messages.length ?? 0;
                        const expanded = expandedId === item.id;
                        return (
                            <div key={item.id} className="strategies-category-card border rounded p-3">
                                <div className="d-flex justify-content-between align-items-start strategies-card-header-row">
                                    <div
                                        className="flex-grow-1"
                                        role="button"
                                        onClick={() => toggleExpand(item.id)}
                                    >
                                        <span className="fw-bold">{item.title}</span>
                                    </div>
                                    {allowEdit && (
                                        <div className="d-flex gap-1">
                                            <Button
                                                size="sm"
                                                variant="outline-secondary"
                                                disabled={index === 0}
                                                onClick={() => moveItem(index, -1)}
                                                aria-label="Move up"
                                            >
                                                ↑
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="outline-secondary"
                                                disabled={index === items.length - 1}
                                                onClick={() => moveItem(index, 1)}
                                                aria-label="Move down"
                                            >
                                                ↓
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="outline-secondary"
                                                onClick={() => openEditModal('edit-item', item)}
                                            >
                                                Edit
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="outline-danger"
                                                onClick={() => removeRow(item, fetchItems)}
                                            >
                                                Remove
                                            </Button>
                                        </div>
                                    )}
                                </div>

                                {expanded && (
                                    <div className="mt-3">
                                        {item.description && <div className="text-muted small mb-3">{item.description}</div>}
                                        <div className="btn-group mb-3">
                                            <Button
                                                size="sm"
                                                variant={expandedTab === 'content' ? 'warning' : 'outline-secondary'}
                                                onClick={() => setExpandedTab('content')}
                                            >
                                                Content
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant={expandedTab === 'discussion' ? 'warning' : 'outline-secondary'}
                                                onClick={() => setExpandedTab('discussion')}
                                            >
                                                Discussion ({messageCount})
                                            </Button>
                                        </div>

                                        {childLoading === item.id || !children ? (
                                            <Loading />
                                        ) : expandedTab === 'content' ? (
                                            <div>
                                                <div className="d-flex justify-content-between align-items-center mb-2">
                                                    <span className="ws-v1-section-label small text-muted">Subitems</span>
                                                    {allowEdit && (
                                                        <Button
                                                            size="sm"
                                                            variant="outline-secondary"
                                                            onClick={() => {
                                                            setModalExistingLinks([]);
                                                            setModalExistingUploads([]);
                                                            setModalInitialLibraryItems([]);
                                                            setModal({ mode: 'create-subitem', parentId: item.id });
                                                        }}
                                                        >
                                                            + Add Subitem
                                                        </Button>
                                                    )}
                                                </div>
                                                {children.subitems.length === 0 ? (
                                                    <p className="text-muted small">No subitems yet.</p>
                                                ) : (
                                                    <div className="d-flex flex-column gap-1">
                                                        {children.subitems.map((sub, subIndex) => (
                                                            <div key={sub.id} className="border rounded p-2">
                                                                <div className="d-flex justify-content-between align-items-start">
                                                                    <div
                                                                        role="button"
                                                                        onClick={() =>
                                                                            setExpandedSubitemId((current) => (current === sub.id ? null : sub.id))
                                                                        }
                                                                    >
                                                                        <div className="fw-bold small">{sub.title}</div>
                                                                        {sub.description && <div className="text-muted small">{sub.description}</div>}
                                                                    </div>
                                                                    {allowEdit && (
                                                                        <div className="d-flex gap-1">
                                                                            <Button
                                                                                size="sm"
                                                                                variant="outline-secondary"
                                                                                disabled={subIndex === 0}
                                                                                onClick={() => moveChild(item.id, 'subitems', subIndex, -1)}
                                                                                aria-label="Move subitem up"
                                                                            >
                                                                                ↑
                                                                            </Button>
                                                                            <Button
                                                                                size="sm"
                                                                                variant="outline-secondary"
                                                                                disabled={subIndex === children.subitems.length - 1}
                                                                                onClick={() => moveChild(item.id, 'subitems', subIndex, 1)}
                                                                                aria-label="Move subitem down"
                                                                            >
                                                                                ↓
                                                                            </Button>
                                                                            <Button
                                                                                size="sm"
                                                                                variant="outline-secondary"
                                                                                onClick={() => openEditModal('edit-subitem', sub)}
                                                                            >
                                                                                Edit
                                                                            </Button>
                                                                            <Button
                                                                                size="sm"
                                                                                variant="outline-danger"
                                                                                onClick={() => removeRow(sub, () => fetchChildren(item.id))}
                                                                            >
                                                                                Remove
                                                                            </Button>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                {expandedSubitemId === sub.id && (
                                                                    <CommentsAndAttachments
                                                                        organizationId={organizationId}
                                                                        teamId={teamId}
                                                                        initiativeId={initiativeId}
                                                                        entityParam="detail_id"
                                                                        entityId={sub.id}
                                                                        allowEdit={allowEdit}
                                                                        currentUserId={currentUserId}
                                                                    />
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                                <CommentsAndAttachments
                                                    organizationId={organizationId}
                                                    teamId={teamId}
                                                    initiativeId={initiativeId}
                                                    entityParam="detail_id"
                                                    entityId={item.id}
                                                    allowEdit={allowEdit}
                                                    currentUserId={currentUserId}
                                                />
                                            </div>
                                        ) : (
                                            <div>
                                                <div className="d-flex justify-content-between align-items-center mb-2">
                                                    <span className="ws-v1-section-label small text-muted">Discussion Topics</span>
                                                    {allowEdit && (
                                                        <Button
                                                            size="sm"
                                                            variant="outline-secondary"
                                                            onClick={() => {
                                                            setModalExistingLinks([]);
                                                            setModalExistingUploads([]);
                                                            setModalInitialLibraryItems([]);
                                                            setModal({ mode: 'create-message', parentId: item.id });
                                                        }}
                                                        >
                                                            + Add Topic
                                                        </Button>
                                                    )}
                                                </div>
                                                {children.messages.length === 0 ? (
                                                    <p className="text-muted small">No topics yet.</p>
                                                ) : (
                                                    <div className="d-flex flex-column gap-1">
                                                        {children.messages.map((message, msgIndex) => (
                                                            <div key={message.id} className="border rounded p-2">
                                                                <div className="d-flex justify-content-between align-items-start">
                                                                    <div
                                                                        role="button"
                                                                        onClick={() =>
                                                                            setExpandedMessageId((current) =>
                                                                                current === message.id ? null : message.id
                                                                            )
                                                                        }
                                                                    >
                                                                        <div className="fw-bold small">{message.title || 'Untitled topic'}</div>
                                                                        {message.description && <div className="text-muted small">{message.description}</div>}
                                                                    </div>
                                                                    {allowEdit && (
                                                                        <div className="d-flex gap-1">
                                                                            <Button
                                                                                size="sm"
                                                                                variant="outline-secondary"
                                                                                disabled={msgIndex === 0}
                                                                                onClick={() => moveChild(item.id, 'messages', msgIndex, -1)}
                                                                                aria-label="Move topic up"
                                                                            >
                                                                                ↑
                                                                            </Button>
                                                                            <Button
                                                                                size="sm"
                                                                                variant="outline-secondary"
                                                                                disabled={msgIndex === children.messages.length - 1}
                                                                                onClick={() => moveChild(item.id, 'messages', msgIndex, 1)}
                                                                                aria-label="Move topic down"
                                                                            >
                                                                                ↓
                                                                            </Button>
                                                                            <Button
                                                                                size="sm"
                                                                                variant="outline-secondary"
                                                                                onClick={() => openEditModal('edit-message', message)}
                                                                            >
                                                                                Edit
                                                                            </Button>
                                                                            <Button
                                                                                size="sm"
                                                                                variant="outline-danger"
                                                                                onClick={() => removeRow(message, () => fetchChildren(item.id))}
                                                                            >
                                                                                Remove
                                                                            </Button>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                {expandedMessageId === message.id && (
                                                                    <CommentsAndAttachments
                                                                        organizationId={organizationId}
                                                                        teamId={teamId}
                                                                        initiativeId={initiativeId}
                                                                        entityParam="detail_id"
                                                                        entityId={message.id}
                                                                        allowEdit={allowEdit}
                                                                        currentUserId={currentUserId}
                                                                    />
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
            </>
            )}

            {modal && (
                <ContentEditorModal
                    show
                    title={modalTitle(modal.mode)}
                    titleLabel={isSubitemModal ? 'Subitem' : isMessageModal ? 'Topic' : 'Title'}
                    titlePlaceholder={isSubitemModal ? 'Subitem *' : isMessageModal ? 'Topic *' : undefined}
                    storyLabel={modal?.mode === 'edit-message' ? 'Description' : 'Story'}
                    storyPlaceholder={isMessageModal ? 'Description (optional)' : undefined}
                    saving={saving || modalLoadingAttachments}
                    showAttachments
                    showLibrary
                    existingLinks={modalExistingLinks}
                    existingUploads={modalExistingUploads}
                    initialLibraryItems={modalInitialLibraryItems}
                    initialValues={
                        modal.editing
                            ? { title: modal.editing.title ?? '', description: modal.editing.description ?? '' }
                            : undefined
                    }
                    onHide={() => setModal(null)}
                    onSubmit={handleModalSubmit}
                />
            )}
        </div>
    );
};
